import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FeedbackModule } from "@/components/quiz/FeedbackModule";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, ExternalLink, Clock, Users, MapPin, CheckCircle, Info, AlertTriangle, Building2, Sparkles, GraduationCap } from "lucide-react";

// Heuristic: is this program beginner-friendly / no-experience-needed?
const isBeginnerFriendly = (p: any) => {
  const hay = `${p.eligibility || ""} ${p.description || ""} ${p.name || ""}`.toLowerCase();
  return /beginner|no prior|no experience|anyone|10\+2|class 12|class 10|open to all|any graduate|any stream/.test(
    hay,
  );
};

// Extract a clean domain from the program's official URL for favicon lookup.
const getSourceDomain = (url?: string) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

const SourceLogo = ({ url, provider }: { url?: string; provider: string }) => {
  const domain = getSourceDomain(url);
  return (
    <div
      className="h-11 w-11 shrink-0 rounded-lg bg-muted flex items-center justify-center border border-border"
      aria-hidden="true"
      title={domain || provider}
    >
      <Building2 className="h-5 w-5 text-muted-foreground" />
    </div>
  );
};
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Programs are loaded at runtime from /programs.json so non-developers
// can edit the listing without touching code. See public/programs.README.md
// pricingType:
//   "free"                          → 100% free (content + certificate)
//   "free_with_paid_certificate"    → Free to learn, certificate may be paid (audit / FA route)
//   "paid"                          → Paid program (e.g., affordable but not free)
type Program = {
  id: number;
  name: string;
  provider: string;
  sector: string;
  mode: string;
  eligibility: string;
  duration: string;
  outcome: string;
  description: string;
  isFree: boolean;
  isGlobal: boolean;
  officialUrl: string;
  note?: string;
  tags?: string[];
  pricingType?: "free" | "free_with_paid_certificate" | "paid";
  accessType?: string;
  subGroup?: string;
  fallbackUrl?: string;
  fallbackLabel?: string;
  extraAudience?: string;
  audience?: "student" | "professional" | "both";
  category?: string;
  cost?: string;
  enrolmentEnds?: string; // ISO date (YYYY-MM-DD) of enrolment deadline for cycle-bound courses
};

// Cycle-bound courses (e.g. SWAYAM/NPTEL) auto-switch to "Cycle Closed"
// once their enrolment deadline has passed — no manual recheck needed.
const isCycleClosed = (p: Program): boolean => {
  if (!p.enrolmentEnds) return false;
  const end = new Date(`${p.enrolmentEnds}T23:59:59`);
  return !isNaN(end.getTime()) && end.getTime() < Date.now();
};

const FALLBACK_PROGRAMS: Program[] = [
  // Government / Indian Programs
  {
    id: 1,
    name: "PMKVY - Pradhan Mantri Kaushal Vikas Yojana",
    provider: "Ministry of Skill Development",
    sector: "Multiple Sectors",
    mode: "Offline",
    eligibility: "10+2 Pass, Age 18-35",
    duration: "3-6 months",
    outcome: "Job Placement",
    description: "Free skill training in multiple sectors with certification and placement assistance.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://www.skillindiadigital.gov.in/pmkvy-landing",
    note: "Fully funded by Government of India",
    tags: ["government", "skill", "job", "training"],
    pricingType: "free",
  },
  {
    id: 2,
    name: "NAPS - National Apprenticeship Promotion Scheme",
    provider: "Ministry of Skill Development",
    sector: "Apprenticeship",
    mode: "Hybrid",
    eligibility: "10+2 Pass, Age 14+",
    duration: "6-36 months",
    outcome: "Apprenticeship",
    description: "Earn while you learn with stipend from ₹5,000 - ₹9,000/month.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://www.apprenticeshipindia.gov.in",
    note: "Earn stipend while learning",
    tags: ["apprenticeship", "earn", "government"],
    pricingType: "free",
    fallbackUrl: "https://msde.gov.in/en/schemes-initiatives/apprenticeship-training/naps",
    fallbackLabel: "MSDE NAPS info page",
  },
  {
    id: 3,
    name: "Skill India Digital Hub",
    provider: "Government of India",
    sector: "Multiple Sectors",
    mode: "Online",
    eligibility: "Anyone",
    duration: "Varies",
    outcome: "Certification",
    description: "Single-window portal for all government skill training schemes and registrations.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://skillindiadigital.gov.in",
    note: "Official government portal",
    tags: ["government", "skill", "portal"],
    pricingType: "free",
  },
  {
    id: 4,
    name: "SWAYAM NPTEL Courses",
    provider: "AICTE / IITs",
    sector: "Multiple Sectors",
    mode: "Online",
    eligibility: "10+2 Pass",
    duration: "4-12 weeks",
    outcome: "Certification",
    description: "Free online courses from IITs. Learning is free, optional paid certificate available.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://onlinecourses.nptel.ac.in",
    note: "Learning free, certificate ₹1000",
    tags: ["nptel", "iit", "certification", "swayam"],
    accessType: "audit",
    pricingType: "free_with_paid_certificate",
  },
  {
    id: 5,
    name: "SWAYAM Courses",
    provider: "Government of India",
    sector: "Multiple Sectors",
    mode: "Online",
    eligibility: "Class 9 to Postgraduate",
    duration: "4-16 weeks",
    outcome: "Certification",
    description: "2000+ free courses from UGC, AICTE, NCERT, NIOS, IGNOU and more.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://swayam.gov.in/explorer",
    note: "India's large free course platform",
    tags: ["swayam", "government", "ugc", "certification"],
    pricingType: "free",
  },
  {
    id: 6,
    name: "NIELIT Free Computer Courses",
    provider: "NIELIT India",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "10+2 Pass",
    duration: "2-6 months",
    outcome: "Certification",
    description: "Free computer literacy and skill courses including CCC, O-Level basics.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://www.nielit.gov.in/content/free-online-courses",
    note: "Government IT training institute",
    tags: ["computer", "it", "government", "nielit"],
    pricingType: "free",
  },
  {
    id: 7,
    name: "Healthcare Sector Training (HSSC)",
    provider: "Healthcare Sector Skill Council",
    sector: "Healthcare",
    mode: "Offline",
    eligibility: "10+2 with Science",
    duration: "3-6 months",
    outcome: "Placement Assistance",
    description: "Free training for General Duty Assistant, Phlebotomy, and more with placement assistance.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://www.healthcare-ssc.in/",
    fallbackUrl: "https://www.skillindiadigital.gov.in/home",
    fallbackLabel: "Find via Skill India Digital Hub",
    note: "Under PMKVY scheme",
    tags: ["healthcare", "health", "placement", "government"],
    pricingType: "free",
  },
  // Global / Online Free Programs
  {
    id: 8,
    name: "Google Digital Marketing Fundamentals",
    provider: "Google Skillshop",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "Anyone",
    duration: "40 hours",
    outcome: "Certification",
    description: "Popular free digital marketing course with Google certificate.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://skillshop.withgoogle.com",
    note: "100% free with certificate",
    tags: ["digital", "marketing", "google", "certification"],
    pricingType: "free",
  },
  {
    id: 9,
    name: "Google IT Support Professional Certificate",
    provider: "Google via Coursera",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "Anyone, No prior experience",
    duration: "6 months",
    outcome: "Certification",
    description: "Free IT support certification (Audit mode free, certificate requires Financial Aid).",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://grow.google/intl/en_in/certificates/",
    note: "Audit free, apply Financial Aid for certificate",
    tags: ["it", "google", "certification", "support"],
    accessType: "audit",
    pricingType: "free_with_paid_certificate",
  },
  {
    id: 10,
    name: "FreeCodeCamp Web Development",
    provider: "FreeCodeCamp",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "Anyone",
    duration: "300+ hours",
    outcome: "Certification",
    description: "Free comprehensive web development training - HTML, CSS, JavaScript, Python.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.freecodecamp.org/learn",
    note: "Completely free with certificates",
    tags: ["coding", "web", "javascript", "python", "it"],
    pricingType: "free",
  },
  {
    id: 11,
    name: "Microsoft Learn",
    provider: "Microsoft",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "Anyone",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Free learning paths for Excel, Azure, AI, Cybersecurity and more.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://learn.microsoft.com",
    note: "Free learning, some exams paid",
    tags: ["microsoft", "excel", "azure", "it", "productivity"],
    accessType: "audit",
    pricingType: "free_with_paid_certificate",
  },
  {
    id: 12,
    name: "AWS Cloud Practitioner Essentials",
    provider: "Amazon Web Services",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "Anyone",
    duration: "6 hours",
    outcome: "Certification",
    description: "Free foundational cloud computing course from AWS.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://aws.amazon.com/training/",
    note: "Free training, exam fee applies",
    tags: ["cloud", "aws", "it", "amazon"],
    accessType: "audit",
    pricingType: "free_with_paid_certificate",
  },
  {
    id: 13,
    name: "IBM SkillsBuild",
    provider: "IBM",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "Anyone",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Free AI, data science, cloud, and cybersecurity courses from IBM.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://skillsbuild.org",
    note: "100% free with digital badges",
    tags: ["ai", "data", "cloud", "ibm", "it"],
    pricingType: "free",
  },
  {
    id: 14,
    name: "Canva Design School",
    provider: "Canva",
    sector: "Design",
    mode: "Online",
    eligibility: "Anyone",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Free graphic design courses covering branding, social media, and visual design.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.canva.com/designschool/",
    note: "100% free with certificates",
    tags: ["design", "graphic", "canva", "branding"],
    pricingType: "free",
  },
  {
    id: 15,
    name: "Khan Academy Financial Literacy",
    provider: "Khan Academy",
    sector: "Finance",
    mode: "Online",
    eligibility: "Anyone",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Free courses on personal finance, budgeting, investing, and economics basics.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.khanacademy.org/economics-finance-domain",
    note: "100% free, no sign-up needed",
    tags: ["finance", "money", "economics", "khan"],
    pricingType: "free",
  },
  {
    id: 16,
    name: "NASSCOM FutureSkills Prime",
    provider: "NASSCOM",
    sector: "IT & Digital",
    mode: "Online",
    eligibility: "Anyone",
    duration: "Self-paced",
    outcome: "Certification",
    description: "AI, ML, Cybersecurity, and emerging tech skilling platform by NASSCOM.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://futureskills.nasscom.in",
    note: "Some courses free, some subsidized",
    tags: ["ai", "ml", "nasscom", "tech", "it"],
    pricingType: "free",
  },
  {
    id: 17,
    name: "IGNOU Distance Learning",
    provider: "Indira Gandhi National Open University",
    sector: "Multiple Sectors",
    mode: "Online",
    eligibility: "Varies by program",
    duration: "6 months - 3 years",
    outcome: "Degree/Diploma",
    description: "Affordable distance learning degrees and diplomas from India's largest open university.",
    isFree: false,
    isGlobal: false,
    officialUrl: "https://www.ignou.ac.in",
    note: "Low-cost, not free but affordable",
    tags: ["ignou", "degree", "diploma", "distance"],
    pricingType: "paid",
  },
  {
    id: 18,
    name: "Coursera (Audit Mode / Financial Aid)",
    provider: "Coursera",
    sector: "Multiple Sectors",
    mode: "Online",
    eligibility: "Anyone",
    duration: "4-12 weeks per course",
    outcome: "Certification",
    description: "Access world-class courses for free (Audit) or apply for 100% Financial Aid for certificates.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.coursera.org",
    note: "Audit free, Financial Aid available for India",
    tags: ["coursera", "certification", "global", "audit"],
    accessType: "audit",
    pricingType: "free_with_paid_certificate",
  },
  // ── Employability After 12th — Workplace Ready ──
  {
    id: 19,
    name: "NPTEL – Enhancing Soft Skills & Personality",
    provider: "NPTEL",
    sector: "Employability After 12th",
    subGroup: "Workplace Ready",
    mode: "Online",
    eligibility: "10+2 students / beginners",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Interview confidence, communication, and workplace skills for freshers.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://nptel.ac.in/courses/109104115",
    note: "100% free NPTEL course",
    tags: ["soft-skills", "communication", "employability", "after12th", "workplace", "interview"],
    pricingType: "free",
  },
  {
    id: 20,
    name: "TCS iON – Career Edge (Young Professional)",
    provider: "TCS iON",
    sector: "Employability After 12th",
    subGroup: "Workplace Ready",
    mode: "Online",
    eligibility: "Freshers / 10+2+",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Resume building, interview prep, email etiquette, and workplace readiness.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://www.tcsion.com/courses/career-edge-young-professional/",
    note: "Free course from TCS",
    tags: ["employability", "after12th", "soft-skills", "interview", "workplace", "tcs"],
    pricingType: "free",
  },
  {
    id: 21,
    name: "TCS iON – Interview & Job Readiness",
    provider: "TCS iON",
    sector: "Employability After 12th",
    subGroup: "Workplace Ready",
    mode: "Online",
    eligibility: "Freshers",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Interview prep, confidence building, and professional communication skills.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://www.tcsion.com/courses/career-edge-interview-and-job-readiness/",
    note: "Free from TCS iON",
    tags: ["employability", "after12th", "interview", "communication", "workplace", "tcs"],
    pricingType: "free",
  },
  // ── Employability After 12th — Digital Marketing ──
  {
    id: 22,
    name: "Google Skillshop – Fundamentals of Digital Marketing",
    provider: "Google Skillshop",
    sector: "Employability After 12th",
    subGroup: "Digital Marketing",
    mode: "Online",
    eligibility: "Beginners / 10+2+",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Core digital marketing skills for entry-level roles and freelancing.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://skillshop.exceedlms.com/student/collection/1830706-fundamentals-of-digital-marketing",
    note: "100% free with Google certificate",
    tags: ["digital", "employability", "after12th", "marketing", "google"],
    pricingType: "free",
  },
  // ── Employability After 12th — Excel & Productivity ──
  {
    id: 23,
    name: "Microsoft Learn – Free Training Hub",
    provider: "Microsoft Learn",
    sector: "Employability After 12th",
    subGroup: "Excel & Productivity",
    mode: "Online",
    eligibility: "Beginners",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Practical workplace tools and digital productivity skills from Microsoft.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://learn.microsoft.com/en-us/training/",
    note: "Free learning, some exams paid",
    tags: ["excel", "productivity", "employability", "after12th", "microsoft"],
    pricingType: "free",
  },
  {
    id: 24,
    name: "Excel Learning – Official Help & Training",
    provider: "Microsoft Support",
    sector: "Employability After 12th",
    subGroup: "Excel & Productivity",
    mode: "Online",
    eligibility: "Beginners",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Excel basics to job-ready spreadsheets — official Microsoft training.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://support.microsoft.com/en-us/excel",
    note: "100% free official resource",
    tags: ["excel", "productivity", "employability", "after12th", "spreadsheet"],
    pricingType: "free",
  },
  // ── Employability After 12th — Tech Starters ──
  {
    id: 25,
    name: "Spoken Tutorial – IIT Bombay",
    provider: "Spoken Tutorial (IIT Bombay)",
    sector: "Employability After 12th",
    subGroup: "Tech Starters",
    mode: "Online",
    eligibility: "Beginners / students",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Learn software and tech basics with guided video tutorials from IIT Bombay.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://spoken-tutorial.org/",
    note: "Free with certificates from IIT Bombay",
    tags: ["tech", "employability", "after12th", "software", "iit"],
    pricingType: "free",
  },
  // English Skills
  {
    id: 26,
    name: "British Council – LearnEnglish",
    provider: "British Council",
    sector: "English Skills",
    mode: "Online",
    eligibility: "All levels",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Practice speaking, listening, grammar & vocabulary.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://learnenglish.britishcouncil.org/",
    note: "100% free learning access",
    tags: ["english", "communication", "speaking", "grammar"],
    pricingType: "free",
  },
  {
    id: 27,
    name: "British Council – Free Online Courses",
    provider: "British Council",
    sector: "English Skills",
    mode: "Online",
    eligibility: "All levels",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Short guided courses to improve English for study/work.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.britishcouncil.org/english/learn-online/courses",
    note: "100% free learning access",
    tags: ["english", "communication", "courses"],
    pricingType: "free",
  },
  {
    id: 28,
    name: "USA Learns – Free Online English Courses",
    provider: "USA Learns",
    sector: "English Skills",
    mode: "Online",
    eligibility: "Beginner to Intermediate",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Structured lessons for everyday English.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.usalearns.org/free-online-english-courses",
    note: "100% free learning access",
    tags: ["english", "communication", "beginner"],
    pricingType: "free",
  },
  {
    id: 29,
    name: "Cambridge English – Activities for Learners",
    provider: "Cambridge English",
    sector: "English Skills",
    mode: "Online",
    eligibility: "By level",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Quick practice activities across skills.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.cambridgeenglish.org/learning-english/activities-for-learners",
    note: "100% free learning access",
    tags: ["english", "communication", "practice"],
    pricingType: "free",
  },
  {
    id: 30,
    name: "SWAYAM – English Communication",
    provider: "SWAYAM (Govt of India)",
    sector: "English Skills",
    mode: "Online",
    eligibility: "All levels",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Communication skills aligned for students.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://onlinecourses.swayam2.ac.in/cec26_hs08/preview",
    note: "100% free with optional certificate",
    tags: ["english", "communication", "swayam", "government"],
    pricingType: "free",
  },
  {
    id: 31,
    name: "NPTEL – English for Competitive Exams",
    provider: "NPTEL",
    sector: "English Skills",
    mode: "Online",
    eligibility: "Intermediate+",
    duration: "Self-paced",
    outcome: "Certification",
    description: "Improve language for exams and job readiness.",
    isFree: true,
    isGlobal: false,
    officialUrl: "https://onlinecourses.nptel.ac.in/noc26_hs74/preview",
    note: "100% free NPTEL course",
    tags: ["english", "communication", "exams", "nptel"],
    pricingType: "free",
  },
  {
    id: 32,
    name: "OpenLearn – Everyday English 1",
    provider: "OpenLearn (Open University)",
    sector: "English Skills",
    mode: "Online",
    eligibility: "Beginner",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Foundations of everyday speaking & listening.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.open.edu/openlearn/languages/everyday-english-1/",
    note: "100% free learning access",
    tags: ["english", "communication", "beginner", "speaking"],
    pricingType: "free",
  },
  {
    id: 33,
    name: "OpenLearn – English: Skills for Learning",
    provider: "OpenLearn (Open University)",
    sector: "English Skills",
    mode: "Online",
    eligibility: "Beginner to Intermediate",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Reading & writing skills for learning and work.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.open.edu/openlearn/education-development/english-skills-learning",
    note: "100% free learning access",
    tags: ["english", "communication", "reading", "writing"],
    pricingType: "free",
  },
  {
    id: 34,
    name: "BBC Learning English",
    provider: "BBC",
    sector: "English Skills",
    mode: "Online",
    eligibility: "All levels",
    duration: "Self-paced",
    outcome: "Learning",
    description: "Short lessons and videos for daily learning.",
    isFree: true,
    isGlobal: true,
    officialUrl: "https://www.bbc.co.uk/learningenglish",
    note: "100% free learning access",
    tags: ["english", "communication", "bbc", "videos"],
    pricingType: "free",
  },
];

const sectors = [
  "All Sectors",
  "English Skills",
  "Employability After 12th",
  "IT & Digital",
  "Healthcare",
  "Multiple Sectors",
  "Apprenticeship",
  "Design",
  "Finance",
  "Tools & Utilities",
];
const modes = ["All Modes", "Online", "Offline", "Hybrid"];
const outcomes = ["All Outcomes", "Job Placement", "Certification", "Apprenticeship", "Degree/Diploma", "Learning"];

const pricingFilters = [
  { value: "free_only", label: "Only 100% Free" },
  { value: "free_to_learn", label: "Free to Learn (Cert. paid)" },
  { value: "paid", label: "Paid" },
  { value: "all", label: "Show all" },
] as const;
type PricingFilter = typeof pricingFilters[number]["value"];

// Sub-group order for Employability After 12th
const employabilitySubGroups = ["Workplace Ready", "Digital Marketing", "Excel & Productivity", "Tech Starters"];

const Programs = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSector, setSelectedSector] = useState("All Sectors");
  const [selectedMode, setSelectedMode] = useState("All Modes");
  const [selectedOutcome, setSelectedOutcome] = useState("All Outcomes");
  const [pricingFilter, setPricingFilter] = useState<PricingFilter>("free_only");
  const [audienceTab, setAudienceTab] = useState<"student" | "professional">("student");
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [tableCategory, setTableCategory] = useState("All Categories");
  const [tableCost, setTableCost] = useState("All");
  const [programs, setPrograms] = useState<Program[]>(FALLBACK_PROGRAMS);
  const [redirectConfirm, setRedirectConfirm] = useState<{
    url: string;
    name: string;
    provider: string;
    pricingType: "free" | "free_with_paid_certificate" | "paid";
    isAudit: boolean;
  } | null>(null);

  useEffect(() => {
    let active = true;
    fetch("/programs.json", { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (active && Array.isArray(data) && data.length > 0) setPrograms(data as Program[]);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const sectorSlug = searchParams.get("sector") || searchParams.get("category");
    if (sectorSlug) {
      const match = sectors.find(
        (s) => s.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-') === sectorSlug
      );
      if (match) setSelectedSector(match);
    }
    const q = searchParams.get("q");
    if (q) setSearchQuery(q);
    const mode = searchParams.get("mode");
    if (mode) setSelectedMode(mode);
    const outcome = searchParams.get("outcome");
    if (outcome) setSelectedOutcome(outcome);
    const pricing = searchParams.get("pricing") as PricingFilter | null;
    if (pricing && ["all", "free_only", "free_to_learn", "paid"].includes(pricing)) {
      setPricingFilter(pricing);
    }
    const audience = searchParams.get("audience");
    if (audience === "student" || audience === "professional") {
      setAudienceTab(audience);
    }
  }, [searchParams]);

  const filteredPrograms = programs.filter((program) => {
    const aud = program.audience || "student";
    // Both tabs currently show the full set: no entry is tagged "professional"
    // yet (27 "student" + 7 "both"), so filtering by "professional" only would
    // hide 27 relevant programs. Both include "student" until real per-entry
    // audience tagging exists.
    const matchesAudience =
      audienceTab === "student"
        ? aud === "student" || aud === "both"
        : aud === "professional" || aud === "both" || aud === "student";
    if (!matchesAudience) return false;
    const tags = (program as any).tags as string[] | undefined;
    const matchesSearch =
      program.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      program.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      program.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tags && tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase())));
    const matchesSector = selectedSector === "All Sectors" || program.sector === selectedSector;
    const matchesMode = selectedMode === "All Modes" || program.mode === selectedMode;
    const matchesOutcome = selectedOutcome === "All Outcomes" || program.outcome === selectedOutcome;
    const pt = (program as any).pricingType || (program.isFree ? "free" : "paid");
    const matchesPricing =
      pricingFilter === "all" ||
      (pricingFilter === "free_only" && pt === "free") ||
      (pricingFilter === "free_to_learn" && pt === "free_with_paid_certificate") ||
      (pricingFilter === "paid" && pt === "paid");
    return matchesSearch && matchesSector && matchesMode && matchesOutcome && matchesPricing;
  });

  const handleApply = (program: Program) => {
    const pt = ((program as any).pricingType || (program.isFree ? "free" : "paid")) as
      | "free"
      | "free_with_paid_certificate"
      | "paid";
    setRedirectConfirm({
      url: program.officialUrl,
      name: program.name,
      provider: program.provider,
      pricingType: pt,
      isAudit: (program as any).accessType === "audit",
    });
  };

  const proceedToOfficialSite = () => {
    if (!redirectConfirm) return;
    window.open(redirectConfirm.url, "_blank", "noopener,noreferrer");
    setRedirectConfirm(null);
  };

  const shareOnWhatsApp = (program: Program) => {
    const text = `Found this free course on we-help.in — no login needed!\n${program.name} | ${program.duration} | ${program.outcome}\nApply here: we-help.in/programs\nA free initiative by SkillSTEM Academy LLP`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const renderProgramCard = (program: Program, index: number) => (
    <motion.div
      key={program.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="p-6 rounded-2xl bg-card border border-border hover:shadow-card transition-all"
    >
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-3">
            <SourceLogo url={program.officialUrl} provider={program.provider} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground font-medium">
                {program.provider}
              </div>
              <h2 className="text-xl font-semibold text-foreground leading-tight">
                {program.name}
              </h2>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {isCycleClosed(program) && (
              <Badge className="bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-100">
                ⏸️ Cycle Closed – Next Session TBA
              </Badge>
            )}
            {(() => {
              const pt = (program as any).pricingType || (program.isFree ? "free" : "paid");
              if (pt === "free") return <Badge variant="free">✅ 100% Free</Badge>;
              if (pt === "free_with_paid_certificate")
                return (
                  <Badge className="bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-100">
                    ⚠️ Free to Learn • Certificate Paid
                  </Badge>
                );
              return <Badge variant="secondary">💰 Paid</Badge>;
            })()}
            <Badge variant={program.mode === "Online" ? "online" : program.mode === "Offline" ? "offline" : "secondary"}>
              {program.mode}
            </Badge>
            {program.isGlobal && <Badge variant="outline">🌍 Global</Badge>}
            <Badge variant="sector">{program.sector}</Badge>
            {(program as any).subGroup && (
              <Badge variant="secondary">{(program as any).subGroup}</Badge>
            )}
            {isBeginnerFriendly(program) && (
              <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 hover:bg-emerald-100">
                <Sparkles className="h-3 w-3 mr-1" />
                Beginner-friendly
              </Badge>
            )}
            {program.extraAudience && (
              <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
                Also useful for: {program.extraAudience}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-3">{program.description}</p>
          {program.note && (
            <p className="text-xs text-primary font-medium mb-4 flex items-center gap-1">
              <Info className="h-3 w-3" />
              {program.note}
            </p>
          )}
          {(program as any).accessType === "audit" && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-foreground">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600" />
              <span>
                Course content is free, but the certificate may be paid. On {program.provider},
                choose <strong>Audit</strong> or apply for <strong>Financial Aid</strong> — do not pay at checkout.
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
            <div className="flex items-start gap-2 rounded-md bg-muted/50 px-2.5 py-2">
              <GraduationCap className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                  Eligibility
                </div>
                <div className="text-foreground leading-snug">{program.eligibility}</div>
              </div>
            </div>
            <div className="flex items-start gap-2 rounded-md bg-muted/50 px-2.5 py-2">
              <Clock className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
                  Duration
                </div>
                <div className="text-foreground leading-snug">{program.duration}</div>
              </div>
            </div>
            <div className="flex items-start gap-2 rounded-md bg-success/10 px-2.5 py-2">
              <CheckCircle className="h-4 w-4 mt-0.5 shrink-0 text-success" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-success font-semibold">
                  Outcome
                </div>
                <div className="text-foreground leading-snug">{program.outcome}</div>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => handleApply(program)}
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium"
            >
              View Official Page
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
            <Link
              to={`/programs/${program.id}`}
              className="ml-4 inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium"
            >
              View details →
            </Link>
          </div>
          {(program as any).fallbackUrl && (
            <p className="mt-2 text-xs text-muted-foreground">
              Portal down?{" "}
              <a
                href={(program as any).fallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium inline-flex items-center gap-1"
              >
                {(program as any).fallbackLabel || "Alternate official source"}
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 lg:justify-center">
          {isCycleClosed(program) ? (
            <Button
              variant="outline"
              className="whitespace-nowrap"
              onClick={() => handleApply(program)}
            >
              View Official Page
              <ExternalLink className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="hero"
              className="whitespace-nowrap"
              onClick={() => handleApply(program)}
            >
              Apply Now
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="whatsapp"
            size="sm"
            onClick={() => shareOnWhatsApp(program)}
          >
            Share on WhatsApp
          </Button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Seo
        title="Free Programs Directory | we-help.in"
        description="Find Your Next Step: Free Courses & Schemes for Students, Graduates & Job-Seekers. Browse curated free education, skill, and employability programs — filter by sector, mode, and outcome."
        path="/programs"
      />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-b from-accent/50 to-background py-12 md:py-16">
          <div className="container">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Explore Free Programs
              </h1>
              <p className="text-lg text-muted-foreground mb-4">
                Find Your Next Step: Free Courses & Schemes for Students, Graduates & Job-Seekers
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                <Info className="h-4 w-4 shrink-0" />
                <span>All "Apply" buttons link directly to official provider websites. we-help.in does not conduct training.</span>
              </div>
              <div className="mt-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Some programs are <strong>free to access</strong> but may charge for the
                  certificate or full features. Look for the <strong>"100% Free"</strong> tag for fully free options,
                  or use the filter below.
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Search & Filters */}
        <section className="border-b border-border bg-background sticky top-16 z-40">
          <div className="container py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search programs, providers, skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
                <select
                  value={pricingFilter}
                  onChange={(e) => setPricingFilter(e.target.value as PricingFilter)}
                  className="h-10 px-3 rounded-lg border border-input bg-background text-sm font-medium"
                  aria-label="Filter by pricing"
                  title="Shows programs where both learning and certificate are free (no payment required)."
                >
                  {pricingFilters.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-input bg-background text-sm"
                  aria-label="Filter by sector"
                >
                  {sectors.map((sector) => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
                <select
                  value={selectedMode}
                  onChange={(e) => setSelectedMode(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-input bg-background text-sm"
                  aria-label="Filter by mode"
                >
                  {modes.map((mode) => (
                    <option key={mode} value={mode}>{mode}</option>
                  ))}
                </select>
                <select
                  value={selectedOutcome}
                  onChange={(e) => setSelectedOutcome(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-input bg-background text-sm"
                  aria-label="Filter by outcome"
                >
                  {outcomes.map((outcome) => (
                    <option key={outcome} value={outcome}>{outcome}</option>
                  ))}
                </select>
              </div>
            </div>
            {pricingFilter === "free_only" && (
              <p className="mt-2 text-xs text-muted-foreground">
                Shows programs where both learning and certificate are free (no payment required).
              </p>
            )}
          </div>
        </section>

        {/* Program Listing */}
        <section className="py-8">
          <div className="container">
            {/* Audience tabs */}
            <div className="mb-5 inline-flex rounded-lg border border-border bg-muted/40 p-1">
              <button
                type="button"
                onClick={() => setAudienceTab("student")}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  audienceTab === "student"
                    ? "bg-background text-foreground shadow-sm font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                After 12th
              </button>
              <button
                type="button"
                onClick={() => setAudienceTab("professional")}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  audienceTab === "professional"
                    ? "bg-background text-foreground shadow-sm font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Job Seekers & Professionals
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Showing {filteredPrograms.length} curated programs
            </p>

            {/* View toggle: Cards / Table */}
            <div className="mb-6 inline-flex rounded-lg border border-border bg-muted/40 p-1" role="group" aria-label="Change view">
              <button
                type="button"
                onClick={() => setViewMode("cards")}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  viewMode === "cards"
                    ? "bg-background text-foreground shadow-sm font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Card View
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  viewMode === "table"
                    ? "bg-background text-foreground shadow-sm font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Table View (All {programs.length})
              </button>
            </div>

            {/* Table view: full catalogue with Category + Cost filters */}
            {viewMode === "table" && (() => {
              const categories = ["All Categories", ...Array.from(new Set(programs.map((p) => p.category || p.sector))).sort()];
              const costs = ["All", ...Array.from(new Set(programs.map((p) => p.cost || (p.isFree ? "Free" : "Paid")))).sort()];
              const rows = programs.filter((p) => {
                const cat = p.category || p.sector;
                const cost = p.cost || (p.isFree ? "Free" : "Paid");
                if (tableCategory !== "All Categories" && cat !== tableCategory) return false;
                if (tableCost !== "All" && cost !== tableCost) return false;
                return true;
              });
              return (
                <div>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <select
                      value={tableCategory}
                      onChange={(e) => setTableCategory(e.target.value)}
                      className="h-11 px-3 rounded-lg border border-input bg-background text-sm"
                      aria-label="Filter table by category"
                    >
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <select
                      value={tableCost}
                      onChange={(e) => setTableCost(e.target.value)}
                      className="h-11 px-3 rounded-lg border border-input bg-background text-sm"
                      aria-label="Filter table by cost"
                    >
                      {costs.map((c) => (
                        <option key={c} value={c}>{c === "All" ? "All Costs" : c}</option>
                      ))}
                    </select>
                    <p className="text-sm text-muted-foreground self-center">
                      {rows.length} of {programs.length} programs
                    </p>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm bg-card">
                      <thead>
                        <tr className="border-b border-border bg-muted/50 text-left">
                          <th scope="col" className="px-4 py-3 font-semibold">Program Name</th>
                          <th scope="col" className="px-4 py-3 font-semibold">Category</th>
                          <th scope="col" className="px-4 py-3 font-semibold">Cost</th>
                          <th scope="col" className="px-4 py-3 font-semibold">Eligibility</th>
                          <th scope="col" className="px-4 py-3 font-semibold">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((p) => (
                          <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                            <td className="px-4 py-3">
                              <Link to={`/programs/${p.id}`} className="font-medium text-foreground hover:text-primary">
                                {p.name}
                              </Link>
                              <span className="block text-xs text-muted-foreground">{p.provider}</span>
                            </td>
                            <td className="px-4 py-3">{p.category || p.sector}</td>
                            <td className="px-4 py-3">
                              {isCycleClosed(p) && (
                                <Badge className="mb-1 bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-100">
                                  ⏸️ Cycle Closed – Next Session TBA
                                </Badge>
                              )}
                              {(() => {
                                const pt = p.pricingType || (p.isFree ? "free" : "paid");
                                if (pt === "free_with_paid_certificate")
                                  return (
                                    <Badge className="bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-100">
                                      ⚠️ Free*
                                    </Badge>
                                  );
                                return (
                                  <Badge variant={(p.cost || (p.isFree ? "Free" : "Paid")) === "Free" ? "default" : "secondary"}>
                                    {(p.cost || (p.isFree ? "Free" : "Paid")) === "Free" ? "✅ Free" : "💰 Paid"}
                                  </Badge>
                                );
                              })()}
                              {p.note && (
                                <span className="mt-1 block text-xs text-muted-foreground max-w-[240px]">
                                  {p.note}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground max-w-xs">{p.eligibility}</td>
                            <td className="px-4 py-3">
                              <a
                                href={p.officialUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary hover:underline"
                                aria-label={`Visit official page for ${p.name} (opens in new tab)`}
                              >
                                Visit <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length === 0 && (
                      <p className="text-center py-8 text-muted-foreground">No programs match these filters.</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Employability After 12th — sub-group sectioned rendering */}
            {viewMode === "cards" && selectedSector === "Employability After 12th" && filteredPrograms.length > 0 && (
              <div className="space-y-10">
                {employabilitySubGroups.map((group) => {
                  const groupPrograms = filteredPrograms.filter((p) => (p as any).subGroup === group);
                  if (groupPrograms.length === 0) return null;
                  return (
                    <div key={group}>
                      <h3 className="text-lg font-semibold text-foreground mb-4 pb-2 border-b border-border flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary inline-block" />
                        {group}
                      </h3>
                      <div className="grid gap-4">
                        {groupPrograms.map((program, index) => renderProgramCard(program, index))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Default flat listing for all other sectors */}
            {viewMode === "cards" && selectedSector !== "Employability After 12th" && (
              <div className="grid gap-4">
                {filteredPrograms.map((program, index) => renderProgramCard(program, index))}
              </div>
            )}

            {viewMode === "cards" && filteredPrograms.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No programs found matching your criteria.</p>
                <Button
                  variant="link"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedSector("All Sectors");
                    setSelectedMode("All Modes");
                    setSelectedOutcome("All Outcomes");
                    setPricingFilter("all");
                  }}
                >
                  Clear filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-muted/30">
          <div className="container">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border max-w-2xl mx-auto">
              <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Disclaimer</p>
                <p className="text-xs text-muted-foreground">
                  we-help.in is a discovery platform only. We do not conduct training, collect fees, or guarantee placements.
                  All links redirect to official provider websites. Always verify program details on the official site before applying.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SkillSTEM bridge */}
        <section className="py-10">
          <div className="container max-w-3xl">
            <div className="rounded-2xl border border-border bg-accent/30 p-6 md:p-8 text-center">
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                Need structured training with placement support?
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-5">
                we-help.in is a free initiative by SkillSTEM Academy LLP. For professional skill courses,
                industry projects, and career guidance, visit skillstem.in
              </p>
              <Button asChild variant="outline">
                <a href="https://skillstem.in" target="_blank" rel="noopener noreferrer">
                  Visit SkillSTEM Academy →
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Feedback module */}
        <section className="py-10">
          <div className="container max-w-3xl">
            <FeedbackModule sourcePage="/programs" />
          </div>
        </section>
      </main>
      <Footer />
      <AlertDialog open={!!redirectConfirm} onOpenChange={(open) => !open && setRedirectConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-primary" />
              Leaving we-help.in
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-1">
                  <p className="font-semibold text-foreground">{redirectConfirm?.name}</p>
                  <p className="text-xs text-muted-foreground">via {redirectConfirm?.provider}</p>
                  <div className="pt-1">
                    {redirectConfirm?.pricingType === "free" && (
                      <Badge variant="free">✅ 100% Free</Badge>
                    )}
                    {redirectConfirm?.pricingType === "free_with_paid_certificate" && (
                      <Badge className="bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-100">
                        ⚠️ Free to Learn • Certificate Paid
                      </Badge>
                    )}
                    {redirectConfirm?.pricingType === "paid" && (
                      <Badge variant="secondary">💰 Paid</Badge>
                    )}
                  </div>
                </div>
                <p>
                  You are being redirected to the provider's website.
                  Pricing and access may vary.
                </p>
                {redirectConfirm?.isAudit && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 space-y-1">
                    <p className="font-medium">Tip to learn for free:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      <li>Look for "<strong>Audit</strong>" or "<strong>Enroll for free</strong>" below the paid button.</li>
                      <li>For a free certificate, apply for <strong>Financial Aid</strong>.</li>
                      <li>Do not enter payment details — we-help.in never asks for fees.</li>
                    </ul>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  We-Help does not control external pricing.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={proceedToOfficialSite}>
              Continue to Official Site
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Programs;
